import { useState, useEffect } from "react";
import { NavLink, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { BarChart3, Gamepad2, LogOut, Menu, X, ArrowLeft, Settings, BookOpen, FileText, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { authService, type AuthUser } from "@/lib/auth";
import { unifiedStorage } from "@/lib/unified-storage";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "../hooks/useAuth";
import { ColorModeSwitch, LogoDoo } from "@/components/ds/ModoDeCor";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/* BARRA LATERAL — a mesma casca do painel do core (frontend/src/components/painel/
 * PainelSidebar.tsx): 240px, bloco do logo com 50px, itens de 32px em 13px, item ativo
 * ROSA (no DS o roxo é ação e o rosa é estado — "onde eu estou" é estado) e, no pé, o
 * bloco da conta com o menu (perfil, sair) e o switch de modo claro/escuro ao lado. */

const eventsMenuItems = [
  { title: "Dashboard", url: "/dashboard", icon: BarChart3 },
  { title: "Mecânicas", url: "/mecanicas", icon: Gamepad2 },
  { title: "Catálogo", url: "/catalogo", icon: BookOpen },
  { title: "Relatórios", url: "/relatorios", icon: FileText },
  { title: "Configurações", url: "/configuracoes", icon: Settings },
];

/* A altura do bloco do logo é a do cabeçalho da área de conteúdo do core (50px). */
export const ALTURA_CABECALHO = 50;
export const LARGURA_SIDEBAR = 240;

const ITEM = "flex h-8 items-center gap-2.5 rounded-[var(--radius)] px-[9px] text-[13px] no-underline transition-colors";
const ITEM_ATIVO = "bg-brand-pink/[0.09] font-semibold text-status-accent";
const ITEM_INATIVO = "font-medium text-[var(--text-strong)] hover:bg-[var(--bg-sunken)]";

function iniciais(nome: string) {
  return nome
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

interface EventsSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
}

export function EventsSidebar({ collapsed, onToggle, className }: EventsSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user: authUser, eventProfile } = useAuth();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  const eventoId = searchParams.get('eventoId');

  useEffect(() => {
    const loadUser = async () => {
      // Try unified storage first (queue_user)
      let currentUser = unifiedStorage.getUser();

      // Fallback to authService if not found
      if (!currentUser) {
        currentUser = authService.getCurrentUser();
      }

      // If still no user but we have a token, try to fetch from API
      if (!currentUser && unifiedStorage.getTokens()?.accessToken) {
        try {
          const { profileService } = await import('../services/profile.service');
          const profile = await profileService.getProfile();
          currentUser = {
            id: profile.id,
            name: profile.name,
            email: profile.email,
            role: profile.type,
            avatarUrl: profile.avatar
          };
          // Save to storage for future use
          unifiedStorage.setUser(currentUser);
        } catch (error) {
          console.error('[EventsSidebar] Failed to fetch user profile:', error);
        }
      }

      if (currentUser) {
        setUser(currentUser);
      }

      setIsLoading(false);
    };

    loadUser();
  }, [authUser]);

  const handleLogout = () => {
    authService.logout();
    navigate("/login", { replace: true });
    toast({
      title: "Logout realizado",
      description: "Você foi desconectado com sucesso",
    });
  };

  const handleBackToEvent = () => {
    if (user?.role === 'agencia') {
      navigate('/agencia');
    } else {
      navigate(eventoId ? `/eventos/editar/${eventoId}` : '/eventos');
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const itens = eventsMenuItems.filter((item) => {
    if (eventProfile?.role === 'staff') {
      return item.url !== '/catalogo' && item.url !== '/configuracoes' && item.url !== '/relatorios';
    }
    return true;
  });
  const podeVoltar = user && (user.role === 'agencia' || user.role === 'admin');

  // No celular, recolhida: só o botão que abre a gaveta.
  if (isMobile && collapsed) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-label="Abrir menu"
        className="fixed left-4 top-4 z-50 flex h-[34px] w-[34px] items-center justify-center rounded-[var(--radius)] border border-[var(--border)] bg-card shadow-lg transition-colors hover:bg-[var(--bg-sunken)]"
      >
        <Menu className="h-4 w-4 text-foreground" />
      </button>
    );
  }

  return (
    <>
      {/* Véu da gaveta no celular */}
      {isMobile && (
        <div
          className="fixed inset-0 z-40 bg-[var(--veu-modal)] transition-opacity duration-300"
          onClick={onToggle}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-full flex-col border-r border-[var(--border)] bg-card",
          className
        )}
        style={{ width: LARGURA_SIDEBAR }}
      >
        <div
          className="flex flex-none items-center justify-between border-b border-[var(--border)] px-4"
          style={{ height: ALTURA_CABECALHO }}
        >
          <LogoDoo className="h-[18px]" />
          {isMobile && (
            <button
              type="button"
              onClick={onToggle}
              aria-label="Fechar menu"
              className="flex h-[30px] w-[30px] items-center justify-center rounded-[var(--radius)] text-[var(--text)] transition-colors hover:bg-[var(--bg-sunken)]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {itens.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.url);
            return (
              <NavLink
                key={item.url}
                to={`${item.url}${location.search}`}
                aria-current={active ? "page" : undefined}
                className={cn(ITEM, active ? ITEM_ATIVO : ITEM_INATIVO)}
              >
                <Icon className="h-4 w-4 flex-none" />
                <span className="truncate">{item.title}</span>
              </NavLink>
            );
          })}
        </nav>

        {podeVoltar && (
          <div className="flex-none border-t border-[var(--border)] p-3">
            <button type="button" onClick={handleBackToEvent} className={cn(ITEM, "w-full", ITEM_INATIVO)}>
              <ArrowLeft className="h-4 w-4 flex-none" />
              <span className="truncate">{user.role === 'agencia' ? 'Voltar à Agência' : 'Voltar ao Evento'}</span>
            </button>
          </div>
        )}

        {/* Bloco da conta, no pé da barra: o menu que leva ao perfil e à saída, e o switch
            de modo claro/escuro ao lado — igual ao core. */}
        <div className="flex flex-none items-center gap-1 border-t border-[var(--border)] p-3">
          <div className="min-w-0 flex-1">
            {isLoading ? (
              <div className="flex animate-pulse items-center gap-2.5 px-1.5 py-1.5">
                <span className="h-8 w-8 flex-none rounded-full bg-muted" />
                <span className="min-w-0 flex-1 space-y-1.5">
                  <span className="block h-3 rounded bg-muted" />
                  <span className="block h-2.5 w-3/4 rounded bg-muted" />
                </span>
              </div>
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2.5 rounded-[var(--radius)] px-1.5 py-1.5 text-left transition-colors hover:bg-[var(--bg-sunken)]"
                  >
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt="" className="h-8 w-8 flex-none rounded-full object-cover" />
                    ) : (
                      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[var(--btn-ink-bg)] text-[12px] font-bold tracking-[0.02em] text-[var(--text-strong)]">
                        {iniciais(user.name)}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-[var(--text-heading)]">{user.name}</span>
                      <span className="block truncate text-[12px] text-muted-foreground">{user.email}</span>
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="w-[196px]">
                  <DropdownMenuItem onSelect={() => navigate(`/perfil${location.search}`)}>
                    <UserIcon className="mr-2.5 h-[17px] w-[17px] text-muted-foreground" />
                    Meu perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleLogout}>
                    <LogOut className="mr-2.5 h-[17px] w-[17px] text-muted-foreground" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
          <ColorModeSwitch />
        </div>
      </aside>
    </>
  );
}
